import React from 'react';
import { FenceDiagram } from './FenceLine';
export type SamplePageId = 'fence-card-system' | 'formula' | 'session-113' | 'session-118' | 'field-card';
const GROUP_COLORS = ['bg-clay', 'bg-amber', 'bg-infield', 'bg-lane'];

/** Eye icon = COACH-SUPERVISED ONLY, as in the volume legend. */
function EyeIcon({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 20 20" className={className} aria-hidden="true" data-id="element-362" data-mp="2ihthu7o7655a">
      <circle cx="10" cy="10" r="9" fill="#fff" stroke="#14213D" strokeWidth="1.6" data-id="element-363" data-mp="2nejusm4pgkmh" />
      <path d="M4.2 10 Q10 4.4 15.8 10 Q10 15.6 4.2 10 Z" fill="none" stroke="#14213D" strokeWidth="1.5" data-id="element-364" data-mp="2sctsb3dwjxb9" />
      <circle cx="10" cy="10" r="2" fill="#14213D" data-id="element-365" data-mp="2wh1fux8mj77w" />
    </svg>;
}

/** The page frame: chalk paper, navy Team band with the four group chips, page number. */
function Frame({
  page,
  kicker,
  title,
  children
}: {
  page: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return <div style={{
    containerType: 'inline-size'
  }} className="w-full" data-id="element-366" data-mp="171le42x14ns1">
      <div className="relative flex aspect-[16/9] w-full flex-col overflow-hidden bg-chalk text-ink" data-id="element-367" data-mp="2wsn9d5ivegid">
        <div className="flex h-[4.2cqw] flex-none items-center justify-between bg-navy px-[3cqw]" data-id="element-368" data-mp="2s1ex81q5oh1i">
          <span className="font-display text-[1.25cqw] font-semibold uppercase tracking-[0.12em] text-chalk" data-id="element-369" data-mp="2nzaa7lu7qinu">
            Track Coach Ready · Vol 1 · Day One Ready
          </span>
          <span className="flex items-center gap-[0.6cqw]" data-id="element-370" data-mp="3fqzmf2cwkaj8">
            {GROUP_COLORS.map(c => <span key={c} className={`h-[1.1cqw] w-[2.4cqw] rounded-[0.3cqw] ${c}`} data-id="element-371" data-mp="3bp4jxuj8jr3v" />)}
            <span className="ml-[1cqw] font-display text-[1.25cqw] font-semibold text-hl" data-id="element-372" data-mp="36q1nuzl8gv81">{page}</span>
          </span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col px-[3cqw] pb-[2.4cqw] pt-[2cqw]" data-id="element-373" data-mp="31qorno8h2bss">
          <p className="text-[1.1cqw] font-bold uppercase tracking-[0.14em] text-clay" data-id="element-374" data-mp="1vcfnei9xb777">{kicker}</p>
          <h4 className="mt-[0.3cqw] font-display text-[3.1cqw] font-bold uppercase leading-[1.05] text-ink" data-id="element-375" data-mp="1qb9t4g0m7bvk">{title}</h4>
          <div className="mt-[1.6cqw] min-h-0 flex-1" data-id="element-376" data-mp="1ru06zwtpvdhw">{children}</div>
        </div>
      </div>
    </div>;
}
function Note({
  children,
  label = 'Coach’s Note',
  tone = 'navy'
}: {
  children: React.ReactNode;
  label?: string;
  tone?: 'navy' | 'hl';
}) {
  const cls = tone === 'navy' ? 'bg-navy text-chalk' : 'bg-hl text-hl-ink';
  return <div className={`rounded-[0.8cqw] px-[1.4cqw] py-[1cqw] ${cls}`} data-id="element-377" data-mp="192z6v5gv1bwr">
      <p className="text-[0.95cqw] font-bold uppercase tracking-[0.14em] opacity-80" data-id="element-378" data-mp="191o51b6z6e8a">{label}</p>
      <p className="mt-[0.3cqw] text-[1.25cqw] font-medium leading-snug" data-id="element-379" data-mp="1dv0ege332egg">{children}</p>
    </div>;
}
function FenceCardSystem() {
  const rules = [['One card per group per day.', 'Setup, blocks, reps, rest, cues.'], ['Low-risk groups self-run.', 'Sprints, Distance, jump drills.'], ['High-risk groups never self-run.', 'Throws, hurdles, high jump, pit jumps.'], ['The coach anchors where the risk is.', 'Keep the others in sight.'], ['One coach = one supervised group.', 'The rest do a low-risk card first.'], ['The captain reads the card aloud.', 'Starts the block on time.'], ['Check-out closes the loop.', 'Initials, equipment, one question.']];
  return <Frame page="P06" kicker="Block A · Framework" title="The Fence Card System: one coach, five event groups" data-id="element-380" data-mp="lh6oo0rcslce">
      <div className="grid h-full grid-cols-[1.15fr_1fr] gap-[2.4cqw]" data-id="element-381" data-mp="gilou2g0k3oh">
        <div className="flex items-center rounded-[1cqw] bg-white px-[1.2cqw] shadow-sm" data-id="element-382" data-mp="uez5588t9z1d">
          <FenceDiagram className="w-full" data-id="element-383" data-mp="pg5tzmgzyk4d" />
        </div>
        <div className="flex flex-col gap-[0.7cqw]" data-id="element-384" data-mp="1463ik3iu9bu3">
          <ol className="grid gap-[0.55cqw]" data-id="element-385" data-mp="145k1ep10irh4">
            {rules.map(([rule, sub], i) => <li key={rule} className="flex gap-[0.8cqw]" data-id="element-386" data-mp="z8bgwhse6bcp" data-mpi={i}>
                <span className="flex h-[1.9cqw] w-[1.9cqw] flex-none items-center justify-center rounded-full bg-navy font-display text-[1.1cqw] font-bold text-chalk" data-id="element-387" data-mp="1d6i5167jgwz6">
                  {i + 1}
                </span>
                <p className="text-[1.2cqw] leading-tight" data-id="element-388" data-mp="32oou53lakoj2">
                  <span className="font-bold" data-id="element-389" data-mp="32m2l0zir60yq">{rule}</span> <span className="text-ink-soft" data-id="element-390" data-mp="37lyggaddwk2o">{sub}</span>
                  {i === 2 && <EyeIcon className="ml-[0.4cqw] inline h-[1.4cqw] w-[1.4cqw] align-[-0.25cqw]" data-id="element-391" data-mp="3cdypqtfcgpbx" />}
                </p>
              </li>)}
          </ol>
          <div className="mt-auto" data-id="element-392" data-mp="3hew38spesv73">
            <Note data-id="element-393" data-mp="2jpko83p1ravm">The card does the talking so you can do the coaching.</Note>
          </div>
        </div>
      </div>
    </Frame>;
}
function Formula() {
  const rows: {
    len: number;
    mins: number[];
  }[] = [{
    len: 90,
    mins: [15, 15, 40, 10, 10]
  }, {
    len: 75,
    mins: [12, 12, 33, 8, 10]
  }, {
    len: 60,
    mins: [10, 10, 27, 5, 8]
  }];
  const blocks = [{
    name: 'Warm-Up',
    cls: 'bg-clay'
  }, {
    name: 'Drills',
    cls: 'bg-amber'
  }, {
    name: 'Main Set by group',
    cls: 'bg-lane'
  }, {
    name: 'Strength',
    cls: 'bg-infield'
  }, {
    name: 'Cool-Down + Check-Out',
    cls: 'bg-navy'
  }];
  const cuts = ['Cut the main set most. Trim warm-up and cool-down by 2–5 minutes at most.', 'In the main set, drop reps, not rest.', 'On 60-minute days, strength becomes a 5-minute finisher.'];
  return <Frame page="P04" kicker="Formula" title="The 90-Minute Practice Formula" data-id="element-394" data-mp="1onabnhsbi1km">
      <div className="flex h-full flex-col gap-[1.3cqw]" data-id="element-395" data-mp="1jpqzqoj3e0vu">
        <div className="flex flex-wrap gap-x-[1.6cqw] gap-y-[0.4cqw]" data-id="element-396" data-mp="1jn4pui8qb9ag">
          {blocks.map(b => <span key={b.name} className="flex items-center gap-[0.5cqw] text-[1.1cqw] font-semibold text-ink-soft" data-id="element-397" data-mp="1enrt95s22nuo">
              <span className={`h-[1.1cqw] w-[1.1cqw] rounded-[0.25cqw] ${b.cls}`} data-id="element-398" data-mp="15pg2n3r6vq28" />
              {b.name}
            </span>)}
        </div>
        <div className="grid gap-[0.9cqw]" data-id="element-399" data-mp="10rxuvgowrgvt">
          {rows.map(r => <div key={r.len} className="flex items-center gap-[1.2cqw]" data-id="element-400" data-mp="303qs9eflyo92">
              <span className="w-[6cqw] flex-none font-display text-[2.4cqw] font-bold leading-none" data-id="element-401" data-mp="2v4np5zj6us3q">
                {r.len}
                <span className="ml-[0.3cqw] text-[1cqw] font-semibold text-ink-soft" data-id="element-402" data-mp="3e7y8n38f1zul">MIN</span>
              </span>
              <div className="flex-1" data-id="element-403" data-mp="3j0iw7n0qbgpo">
                <div className="flex h-[3.4cqw] gap-[0.3cqw]" style={{
              width: `${r.len / 90 * 100}%`
            }} data-id="element-404" data-mp="3j1k8futhe98z">
                  {r.mins.map((m, i) => <span key={i} className={`flex items-center justify-center rounded-[0.4cqw] font-display text-[1.4cqw] font-bold text-white ${blocks[i].cls}`} style={{
                flex: m
              }} data-id="element-405" data-mp="3a879ab4tqbeg" data-mpi={i}>
                      {m}
                    </span>)}
                </div>
              </div>
            </div>)}
        </div>
        <div className="grid grid-cols-3 gap-[1cqw]" data-id="element-406" data-mp="3sw2kbn91xb3v">
          {cuts.map(c => <p key={c} className="rounded-[0.8cqw] bg-white px-[1.1cqw] py-[0.9cqw] text-[1.15cqw] leading-snug shadow-sm" data-id="element-407" data-mp="2ci2lygputtiq">
              {c}
            </p>)}
        </div>
        <div className="mt-auto grid gap-[0.8cqw]" data-id="element-408" data-mp="29xwgg4lldv02">
          <Note data-id="element-409" data-mp="2504o59h1c6vc">Write the block start times on the whiteboard: 3:15 · 3:30 · 3:45 · 4:25 · 4:35. Athletes learn the rhythm in a week and start moving without being told.</Note>
          <Note label="Quick Win" tone="hl" data-id="element-410" data-mp="2ixtaklqr3m2i">
            Tape a 60-minute version to the back of your clipboard. Short days stop being a crisis.
          </Note>
        </div>
      </div>
    </Frame>;
}
function Session113() {
  const timeline = [['0–15', 'Warm-up · Session 1.02'], ['15–30', 'Drills · Session 1.04'], ['30–70', 'Main set by group'], ['70–80', 'Strength · Session 1.06'], ['80–90', 'Cool-down + Card Check-Out']];
  const groups = [{
    name: 'Sprints',
    cls: 'bg-clay',
    work: '6 x 20 m falling start, 90 s rest · 3 x 50 m build-up, 2 min rest',
    eye: false
  }, {
    name: 'Hurdles',
    cls: 'bg-clay',
    work: 'Min 50–70: rhythm runs over 4 low barriers x 6',
    eye: true
  }, {
    name: 'Jumps',
    cls: 'bg-amber',
    work: 'Ankle hops 2 x 10 · skip for height 3 x 20 m · standing long jump x 5',
    eye: false
  }, {
    name: 'Throws',
    cls: 'bg-infield',
    work: 'Min 30–50: standing puts x 8, chest passes x 8',
    eye: true
  }, {
    name: 'Distance',
    cls: 'bg-lane',
    work: '15 minutes easy, then 4 x 100 m strides',
    eye: false
  }];
  return <Frame page="P30" kicker="Session 1.13 · Day 6 · All groups · Foundation · 90 min" title="Your First Full Carded Practice" data-id="element-411" data-mp="w0ekx83402de">
      <div className="grid h-full grid-cols-[0.75fr_1.6fr_1fr] gap-[1.8cqw]" data-id="element-412" data-mp="10wlt77iz9pyi">
        <ol className="grid content-start gap-[0.6cqw]" data-id="element-413" data-mp="14yqixv6b22fv">
          {timeline.map(([t, b]) => <li key={t} className="rounded-[0.6cqw] border-l-[0.5cqw] border-navy bg-white px-[0.9cqw] py-[0.6cqw] shadow-sm" data-id="element-414" data-mp="19rjoumdifgtj">
              <span className="block font-display text-[1.35cqw] font-bold leading-none" data-id="element-415" data-mp="1erxlz9dkp6cs">{t} min</span>
              <span className="text-[1.05cqw] text-ink-soft" data-id="element-416" data-mp="1jncz1z3ray85">{b}</span>
            </li>)}
        </ol>
        <div className="grid content-start gap-[0.6cqw]" data-id="element-417" data-mp="396bi72vgkfg7">
          {groups.map(g => <div key={g.name} className="flex overflow-hidden rounded-[0.6cqw] bg-white shadow-sm" data-id="element-418" data-mp="393yt85ra94tn">
              <span className={`flex w-[8.2cqw] flex-none items-center justify-center gap-[0.4cqw] font-display text-[1.25cqw] font-bold uppercase text-white ${g.cls}`} data-id="element-419" data-mp="2z85gal9nj9bc">
                {g.name}
              </span>
              <p className="flex flex-1 items-center gap-[0.6cqw] px-[0.9cqw] py-[0.65cqw] text-[1.1cqw] leading-snug" data-id="element-420" data-mp="3rwjcqwl3dxks">
                {g.eye && <EyeIcon className="h-[1.6cqw] w-[1.6cqw] flex-none" data-id="element-421" data-mp="3mz1477azlkdb" />}
                <span data-id="element-422" data-mp="3i6hpu84h6l7g">
                  {g.work}
                  {g.eye && <span className="font-bold text-clay" data-id="element-423" data-mp="3i4wu4f6fcw80"> COACH-SUPERVISED ONLY</span>}
                </span>
              </p>
            </div>)}
          <div className="mt-[0.4cqw] rounded-[0.6cqw] border-[0.15cqw] border-dashed border-navy/40 px-[1cqw] py-[0.7cqw] text-[1.1cqw] leading-snug" data-id="element-424" data-mp="eiy87yt8lk4t">
            <span className="font-bold" data-id="element-425" data-mp="9pd55scsdzyr">Athlete card line:</span> “Captain reads the card. Finished early? Repeat the main set at half reps until the whistle. No barriers, implements or bar.”
          </div>
        </div>
        <div className="flex flex-col gap-[0.8cqw] text-[1.1cqw] leading-snug" data-id="element-426" data-mp="2d2xfv2wa86ec">
          <p data-id="element-427" data-mp="2d0ughs98p0wy">
            <span className="font-bold" data-id="element-428" data-mp="284drkvu7uiit">Cues:</span> “Read it, do it, initial it.” · “Quality first.”
          </p>
          <p data-id="element-429" data-mp="2m3dcptiisfa8">
            <span className="font-bold" data-id="element-430" data-mp="2m2twqicfk0yu">Watch for:</span> groups waiting for you → <span className="font-bold" data-id="element-431" data-mp="2i2jpz3gtd6ix">Fix:</span> captain starts on the whiteboard time.
          </p>
          <p data-id="element-432" data-mp="2vtf16mesbzpo">
            <span className="font-bold" data-id="element-433" data-mp="2vtychk9xjpuq">Easier:</span> 75 minutes. <span className="font-bold" data-id="element-434" data-mp="2vvt056cl5w2i">Harder:</span> +2 reps per main set.
          </p>
          <div className="mt-auto rounded-[0.8cqw] bg-navy px-[1.1cqw] py-[0.9cqw] text-chalk" data-id="element-435" data-mp="2qvdii2l9u2eq">
            <p className="text-[0.95cqw] font-bold uppercase tracking-[0.14em] text-hl" data-id="element-436" data-mp="35s4b9t0fwfvd">Level Dial</p>
            <p className="mt-[0.3cqw]" data-id="element-437" data-mp="30qoptzuco7cg">
              <b data-id="element-438" data-mp="3ke1ppdyq0dpq">Y</b> 60 min, half reps · <b data-id="element-439" data-mp="3kfwb0tdwm7uc">MS</b> 75 min · <b data-id="element-440" data-mp="3kfcsbagfjff9">HS</b> 90 min
            </p>
          </div>
        </div>
      </div>
    </Frame>;
}
function Session118() {
  const stations = [['Accelerations', '5 x 20 m, falling start'], ['Form drills', 'Session 1.04 drills 1–4'], ['Bodyweight strength', '30 s each, twice'], ['Core', 'Session 1.07, exercises 1–3'], ['Grass jumps', 'Ankle hops, land and freeze'], ['Cone agility', '5 m square, 4 laps each way']];
  return <Frame page="P38" kicker="Session 1.18 · Team · Foundation and Build · 75 min" title="Large-Group Station Circuit: 40 athletes, 6 stations" data-id="element-441" data-mp="35hkecmqj6z36">
      <div className="grid h-full grid-cols-[1.6fr_1fr] gap-[2.2cqw]" data-id="element-442" data-mp="2rrqoq277case">
        <div className="relative grid grid-cols-3 grid-rows-2 gap-[1cqw]" data-id="element-443" data-mp="2wps86o8jic4e">
          {stations.map(([name, work], i) => <div key={name} className="flex flex-col rounded-[0.8cqw] border-t-[0.5cqw] border-clay bg-white px-[1cqw] py-[0.9cqw] shadow-sm" data-id="element-444" data-mp="2hshx3fzt7897" data-mpi={i}>
              <span className="flex h-[2.4cqw] w-[2.4cqw] items-center justify-center rounded-full bg-navy font-display text-[1.3cqw] font-bold text-chalk" data-id="element-445" data-mp="23mpr8fzjn3wo">
                {i + 1}
              </span>
              <span className="mt-[0.6cqw] font-display text-[1.5cqw] font-bold uppercase leading-tight" data-id="element-446" data-mp="1tvv65ey1aact">{name}</span>
              <span className="mt-[0.2cqw] text-[1.1cqw] leading-snug text-ink-soft" data-id="element-447" data-mp="1oycu3fopzf0l">{work}</span>
            </div>)}
          <span className="absolute left-1/2 top-1/2 flex h-[6cqw] w-[6cqw] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-hl text-hl-ink shadow-md ring-[0.4cqw] ring-chalk" data-id="element-448" data-mp="2mkai1slvccjx">
            <span className="font-display text-[1.7cqw] font-bold leading-none" data-id="element-449" data-mp="2coyy8vvlsf5b">7 + 1</span>
            <span className="text-[0.85cqw] font-bold uppercase" data-id="element-450" data-mp="27pvv5gz6oizz">min</span>
          </span>
        </div>
        <div className="flex flex-col gap-[0.8cqw] text-[1.1cqw] leading-snug" data-id="element-451" data-mp="115mjpawgt3sf">
          <p data-id="element-452" data-mp="w72yjcpp3w14">
            <span className="font-bold" data-id="element-453" data-mp="waqs2bv54ifu">Setup:</span> six stations in a horseshoe, 10 m apart, a cone and a card at each. 6–7 athletes per station.
          </p>
          <p data-id="element-454" data-mp="3cw435qhxzzpk">
            <span className="font-bold" data-id="element-455" data-mp="3cvlzi8syaj7m">Rotate:</span> clockwise on 1 long whistle.
          </p>
          <p data-id="element-456" data-mp="3htdtr9h2ihez">
            <span className="font-bold" data-id="element-457" data-mp="3hsulkkh7z7e5">Athlete card line:</span> “Captain reads the station card. Work until 1 long whistle. Rotate clockwise.”
          </p>
          <p data-id="element-458" data-mp="3rkqjnvjb2xid">
            <span className="font-bold" data-id="element-459" data-mp="3rixdehclufaw">Cues:</span> “Every rep counts.” · “Rotate fast, start fast.”
          </p>
          <p data-id="element-460" data-mp="3vjzaosmyoha8">
            <span className="font-bold" data-id="element-461" data-mp="3vjr1pbtylw4b">Watch for:</span> one station finishing early and drifting → <span className="font-bold" data-id="element-462" data-mp="4fsm42tx1nus">Fix:</span> “Finished? Repeat the first exercise.”
          </p>
          <p data-id="element-463" data-mp="9dklfhww4c90">
            <span className="font-bold" data-id="element-464" data-mp="9c9fpcjvwuf9">Easier:</span> half reps, same 7 minutes. <span className="font-bold" data-id="element-465" data-mp="267xfzjuz6v85">Harder:</span> 2-second pause on every strength rep.
          </p>
          <div className="mt-auto rounded-[0.8cqw] bg-navy px-[1.1cqw] py-[0.9cqw] text-chalk" data-id="element-466" data-mp="2b68rdz0x5dsh">
            <p className="text-[0.95cqw] font-bold uppercase tracking-[0.14em] text-hl" data-id="element-467" data-mp="5fvbxj85j7yr">Level Dial</p>
            <p className="mt-[0.3cqw]" data-id="element-468" data-mp="htt8zen1ant">
              <b data-id="element-469" data-mp="ei4jq1c1cn8g">Y</b> 5 min per station, half reps · <b data-id="element-470" data-mp="eh3b03ihsctw">MS</b> as written · <b data-id="element-471" data-mp="ehbo9wij1k5m">HS</b> 7 min, full reps
            </p>
          </div>
        </div>
      </div>
    </Frame>;
}
function FieldCard() {
  const boxes: {
    title: string;
    body: string;
    cls?: string;
    eye?: boolean;
  }[] = [{
    title: 'Pre-practice checklist',
    body: '☐ Cards posted at every station ☐ Block times on the whiteboard ☐ Throws sector clear ☐ Supervising coach named ☐ Weather checked against policy'
  }, {
    title: 'Formula (minutes)',
    body: '90: 15 · 15 · 40 · 10 · 10\n75: 12 · 12 · 33 · 8 · 10\n60: 10 · 10 · 27 · 5 · 8'
  }, {
    title: 'Easier / Harder',
    body: 'Easier: fewer reps · shorter distance · longer rest. Harder: 1–2 more reps · 10 m more · rolling start.'
  }, {
    title: 'Universal cues',
    body: '“Tall hips.” “Quiet feet.” “Push the ground back.” “Easy means easy.” “Land and freeze.”'
  }, {
    title: 'Whistle code',
    body: '1 short = go · 2 short = stop and look · 1 long = rotate · 3 long = to the fence'
  }, {
    title: 'Never',
    body: 'Throws, hurdles, high jump or pit jumps without a coach · running toward walls · training through pain.',
    cls: 'bg-clay text-white',
    eye: true
  }];
  return <Frame page="P50" kicker="Field card · print it for your clipboard" title="Day One Field Card" data-id="element-472" data-mp="2g1e1arkc1ltv">
      <div className="relative h-full rounded-[1.2cqw] border-[0.25cqw] border-navy bg-white p-[1.2cqw]" data-id="element-473" data-mp="33rgp2vh7s5mo">
        <span className="absolute left-1/2 top-[-1.1cqw] h-[2cqw] w-[2cqw] -translate-x-1/2 rounded-full border-[0.25cqw] border-navy bg-chalk" data-id="element-474" data-mp="2tv5dy8td1ltt" />
        <div className="grid h-full grid-cols-3 grid-rows-[1fr_1fr_auto] gap-[0.9cqw]" data-id="element-475" data-mp="2ysx3iw6j8vuu">
          {boxes.map(b => <div key={b.title} className={`rounded-[0.7cqw] px-[1cqw] py-[0.8cqw] ${b.cls ?? 'bg-chalk-light ring-1 ring-chalk-dark'}`} data-id="element-476" data-mp="1ienh47luph6n">
              <p className="flex items-center gap-[0.5cqw] font-display text-[1.25cqw] font-bold uppercase" data-id="element-477" data-mp="3q1gt04ip5hij">
                {b.eye && <EyeIcon className="h-[1.6cqw] w-[1.6cqw]" data-id="element-478" data-mp="3l2docl6mpdb3" />}
                {b.title}
              </p>
              <p className="mt-[0.3cqw] whitespace-pre-line text-[1.08cqw] leading-snug" data-id="element-479" data-mp="3gavmzw0p1apl">{b.body}</p>
            </div>)}
          <div className="col-span-3 rounded-[0.7cqw] bg-hl px-[1cqw] py-[0.7cqw] text-hl-ink" data-id="element-480" data-mp="3bckcrk6hky6u">
            <p className="text-[1.1cqw] leading-snug" data-id="element-481" data-mp="36f2dkli9txc1">
              <span className="font-display font-bold uppercase" data-id="element-482" data-mp="32bmfdkqjzox8">Quick access</span> · Warm-ups 1.01–1.03 · Drills 1.04 · Cool-down 1.05 · Strength 1.06–1.07 · Days 1–10: 1.08–1.17 · Big group 1.18 · Gym 1.19 · Games 1.20
            </p>
          </div>
        </div>
      </div>
    </Frame>;
}
const PAGES: Record<SamplePageId, () => JSX.Element> = {
  'fence-card-system': FenceCardSystem,
  formula: Formula,
  'session-113': Session113,
  'session-118': Session118,
  'field-card': FieldCard
};

/**
 * A Volume 1 page drawn in code (landscape 16:9), sized to its container with
 * container-query units so it scales from the carousel to the lightbox.
 */
export function SamplePage({
  id
}: {
  id: SamplePageId;
}) {
  const Page = PAGES[id];
  return <Page data-id="element-483" data-mp="17wpe3b449lx7" />;
}